/**
 * Dữ liệu mẫu và máy chủ giả của màn Cấu hình vai trò và ngăn Gán vai trò
 * (G2-D1-4, ADR-0004).
 *
 * Đóng vai backend khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`.
 * Máy chủ giả **từ chối y như hợp đồng** — cùng mã HTTP, cùng `pointer` — để
 * nhánh lỗi chạy được khi chưa có backend:
 *
 * - Tạo vai trò mã `KE_TOAN` (trùng, `409` trỏ `/code`).
 * - Phân quyền có `ekyc:profile:export`: `422` trỏ `/permissions/{i}` — diễn
 *   tình huống module vừa bị gỡ khỏi tổ chức trong lúc ngăn đang mở.
 * - Gán vai trò cho **Hoàng Đức Anh** (`u-016`): lần lưu đầu nhận `412
 *   ROLE_VERSION_CONFLICT` vì "một quản trị khác" vừa đổi vai trò của người
 *   này ngay sau lúc ngăn đọc. Tải lại rồi lưu lần nữa thì qua.
 * - `u-004` (tài khoản mẫu `tenant…`) tự thêm vai trò cho mình: `403
 *   SELF_ROLE_CHANGE_FORBIDDEN`; chỉ gỡ bớt thì được (ngoại lệ của ADR-0004).
 *
 * Có đủ nhánh xấu: vai trò chưa có quyền nào, vai trò tên rất dài, vai trò giữ
 * quyền của module đã gỡ (`rpa:*`), vai trò chưa ai dùng.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import { MAU_MA_VAI_TRO } from '../modules/vai-tro/nghiep-vu/vai-tro';
import type {
  Permission,
  PermissionPage,
  Role,
  RoleCreate,
  RolePage,
  UserRoles,
  UserRolesCoPhienBan,
} from '../modules/vai-tro/types';

const quyen = (code: string, module_name: string, description: string): Permission => ({
  code,
  module_code: code.split(':')[0] ?? code,
  module_name,
  description,
});

const OCR = 'Xử lý chứng từ OCR';
const EKYC = 'Định danh eKYC';
const KB = 'Tri thức doanh nghiệp';
const TRO_LY = 'Trợ lý AI';
// Nhóm quyền của chính nền tảng (ADR-0104): server luôn trả kèm, kể cả khi tổ chức chưa cài module nào.
const QUAN_LY_MODULE = 'Quản lý module';

export const DANH_MUC_QUYEN: Permission[] = [
  quyen('core:module:install', QUAN_LY_MODULE, 'Cài, nâng cấp và gỡ module'),
  quyen('core:module:read', QUAN_LY_MODULE, 'Xem danh sách module'),
  quyen('core:module:submit', QUAN_LY_MODULE, 'Nộp gói module'),
  quyen('ocr:document:read', OCR, 'Xem chứng từ và kết quả nhận dạng'),
  quyen('ocr:document:upload', OCR, 'Tải chứng từ lên để nhận dạng'),
  quyen('ocr:document:approve', OCR, 'Xác nhận kết quả nhận dạng'),
  quyen('ocr:document:delete', OCR, 'Xoá chứng từ'),
  quyen('ocr:schema:update', OCR, 'Sửa mẫu chứng từ và trường cần đọc'),
  quyen('ekyc:profile:read', EKYC, 'Xem hồ sơ định danh'),
  quyen('ekyc:profile:approve', EKYC, 'Duyệt hoặc từ chối hồ sơ định danh'),
  quyen('ekyc:profile:export', EKYC, 'Xuất hồ sơ định danh ra tệp'),
  quyen('kb:document:read', KB, 'Đọc tài liệu trong kho tri thức'),
  quyen('kb:document:upload', KB, 'Nạp tài liệu vào kho tri thức'),
  quyen('kb:collection:manage', KB, 'Tạo, đổi tên và xoá bộ tri thức'),
  quyen('assistant:chat:use', TRO_LY, 'Hỏi đáp với trợ lý AI'),
  quyen('assistant:history:read', TRO_LY, 'Xem lịch sử hỏi đáp của mọi người'),
];

/** Quyền hợp đồng cho là "không gán được" ở lần lưu — xem đầu tệp. */
const QUYEN_VUA_GO = 'ekyc:profile:export';

type VaiTroGoc = Omit<Role, 'user_count'>;

const vaiTro = (so: number, code: string, name: string, permissions: string[]): VaiTroGoc => ({
  id: `r-${String(so).padStart(2, '0')}`,
  code,
  name,
  permissions,
  created_at: `2026-09-${String(2 + so * 3).padStart(2, '0')}T02:00:00Z`,
});

const VAI_TRO: VaiTroGoc[] = [
  vaiTro(1, 'DUYET_EKYC', 'Duyệt hồ sơ định danh', ['ekyc:profile:read', 'ekyc:profile:approve']),
  vaiTro(2, 'KE_TOAN', 'Kế toán viên', ['ocr:document:read', 'ocr:document:upload', 'assistant:chat:use']),
  vaiTro(3, 'KE_TOAN_TRUONG', 'Kế toán trưởng', [
    'ocr:document:read',
    'ocr:document:upload',
    'ocr:document:approve',
    'ocr:document:delete',
    'ocr:schema:update',
    'assistant:chat:use',
    'assistant:history:read',
  ]),
  vaiTro(
    4,
    'KIEM_SOAT_NOI_BO_CHI_NHANH_MIEN_TRUNG',
    'Kiểm soát nội bộ và đối soát chứng từ chi nhánh miền Trung — chỉ xem, không sửa',
    ['ocr:document:read', 'ekyc:profile:read', 'kb:document:read', 'assistant:history:read'],
  ),
  vaiTro(5, 'NHAN_VIEN_MOI', 'Nhân viên mới', []),
  // Giữ quyền của module RPA đã gỡ khỏi tổ chức: danh mục không còn `rpa:*`.
  vaiTro(6, 'VAN_HANH_RPA', 'Vận hành RPA', ['rpa:bot:run', 'rpa:bot:read', 'kb:document:read']),
];

/** Vai trò lớp 2 của từng người (id theo `mock/nguoi-dung.ts`). */
const GAN_BAN_DAU: Record<string, string[]> = {
  'u-004': ['r-03'],
  'u-010': ['r-02', 'r-06'],
  'u-012': ['r-02', 'r-04'],
  'u-016': ['r-02'],
  'u-017': ['r-02'],
  'u-018': ['r-01', 'r-06'],
  'u-020': ['r-02'],
  'u-022': ['r-01'],
  'u-024': ['r-06'],
};

/** Tài khoản mẫu `tenant…` của trang đăng nhập — "chính mình" của máy chủ giả. */
const TOI = 'u-004';
/** Người diễn `412`: xem đầu tệp. */
const NGUOI_XUNG_DOT = 'u-016';

let cacVaiTro: VaiTroGoc[] = structuredClone(VAI_TRO);
const ganVaiTro = new Map<string, string[]>(Object.entries(structuredClone(GAN_BAN_DAU)));
const phienBan = new Map<string, number>();
let daDienXungDot = false;
let soTao = 100;

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const etag = (userId: string) => `"v${phienBan.get(userId) ?? 1}"`;
const tangPhienBan = (userId: string) => phienBan.set(userId, (phienBan.get(userId) ?? 1) + 1);

const demNguoi = (roleId: string) =>
  [...ganVaiTro.values()].filter((ds) => ds.includes(roleId)).length;

const kemSoNguoi = (r: VaiTroGoc): Role => structuredClone({ ...r, user_count: demNguoi(r.id) });

function tim(id: string): VaiTroGoc {
  const r = cacVaiTro.find((x) => x.id === id);
  if (!r) throw loi('NOT_FOUND', 404, 'Vai trò này không còn. Tải lại danh sách rồi thử lại.');
  return r;
}

/** `422` cho mã quyền không gán được, `pointer` theo vị trí trong mảng đã gửi. */
function kiemQuyen(ds: readonly string[]): void {
  const coTrongDanhMuc = new Set(DANH_MUC_QUYEN.map((q) => q.code));
  const sai = ds.flatMap((ma, i) =>
    !coTrongDanhMuc.has(ma) || ma === QUYEN_VUA_GO
      ? [{ pointer: `#/permissions/${i}`, detail: `Quyền ${ma} không còn gán được: module của nó vừa bị gỡ khỏi tổ chức.` }]
      : [],
  );
  if (sai.length > 0) {
    throw loi('PERMISSION_NOT_AVAILABLE', 422, 'Có quyền không gán được cho vai trò. Bỏ chọn các quyền được đánh dấu rồi lưu lại.', sai);
  }
}

export const mayChuVaiTro = {
  /** `GET /api/v1/roles` — sắp theo `code`, cursor là vị trí bắt đầu của trang sau. */
  danhSach({ limit, cursor }: { limit: number; cursor?: string }): RolePage {
    const sap = [...cacVaiTro].sort((a, b) => a.code.localeCompare(b.code));
    const tu = cursor ? Number(cursor) : 0;
    return {
      items: sap.slice(tu, tu + limit).map(kemSoNguoi),
      next_cursor: tu + limit < sap.length ? String(tu + limit) : null,
    };
  },

  /** `GET /api/v1/permissions`. */
  danhMucQuyen(): PermissionPage {
    return { items: structuredClone(DANH_MUC_QUYEN), next_cursor: null };
  },

  /** `POST /api/v1/roles`. */
  tao(du: RoleCreate): Role {
    if (!MAU_MA_VAI_TRO.test(du.code)) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/code', detail: 'Mã chỉ gồm chữ in hoa, số và gạch dưới, bắt đầu bằng chữ.' },
      ]);
    }
    if (cacVaiTro.some((r) => r.code === du.code)) {
      throw loi('ROLE_ALREADY_EXISTS', 409, `Mã ${du.code} đã có trong tổ chức.`, [
        { pointer: '#/code', detail: `Mã ${du.code} đã có. Đặt mã khác.` },
      ]);
    }
    kiemQuyen(du.permissions ?? []);
    soTao += 1;
    const moi: VaiTroGoc = {
      id: `r-${soTao}`,
      code: du.code,
      name: du.name.trim(),
      permissions: [...(du.permissions ?? [])],
      created_at: new Date().toISOString(),
    };
    cacVaiTro = [...cacVaiTro, moi];
    return kemSoNguoi(moi);
  },

  /** `PATCH /api/v1/roles/{id}`. */
  doiTen(id: string, name: string): Role {
    const r = tim(id);
    if (!name.trim()) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/name', detail: 'Tên vai trò không được để trống.' },
      ]);
    }
    const moi = { ...r, name: name.trim() };
    cacVaiTro = cacVaiTro.map((x) => (x.id === id ? moi : x));
    return kemSoNguoi(moi);
  },

  /** `PUT /api/v1/roles/{id}/permissions`. */
  ganQuyen(id: string, permissions: string[]): Role {
    const r = tim(id);
    kiemQuyen(permissions);
    const moi = { ...r, permissions: [...permissions] };
    cacVaiTro = cacVaiTro.map((x) => (x.id === id ? moi : x));
    return kemSoNguoi(moi);
  },

  /** `DELETE /api/v1/roles/{id}` — gỡ khỏi mọi người đang có. */
  xoa(id: string): void {
    tim(id);
    cacVaiTro = cacVaiTro.filter((x) => x.id !== id);
    for (const [userId, ds] of ganVaiTro) {
      if (ds.includes(id)) {
        ganVaiTro.set(userId, ds.filter((x) => x !== id));
        tangPhienBan(userId);
      }
    }
  },

  /** `GET /api/v1/users/{id}/roles` kèm `ETag`. */
  vaiTroCuaNguoi(userId: string): UserRolesCoPhienBan {
    const kq = { user_id: userId, role_ids: [...(ganVaiTro.get(userId) ?? [])], etag: etag(userId) };
    if (userId === NGUOI_XUNG_DOT && !daDienXungDot) {
      // "Quản trị khác" thêm Duyệt hồ sơ định danh ngay sau lúc ngăn đọc.
      daDienXungDot = true;
      ganVaiTro.set(userId, [...new Set([...(ganVaiTro.get(userId) ?? []), 'r-01'])]);
      tangPhienBan(userId);
    }
    return kq;
  },

  /** `PUT /api/v1/users/{id}/roles` với `If-Match`. */
  ganVaiTroChoNguoi(userId: string, roleIds: string[], ifMatch: string): UserRolesCoPhienBan {
    if (!ifMatch) {
      throw loi('PRECONDITION_REQUIRED', 428, 'Thiếu phiên bản vai trò (If-Match). Tải lại rồi lưu lại.');
    }
    if (ifMatch !== etag(userId)) {
      throw loi(
        'ROLE_VERSION_CONFLICT',
        412,
        'Vai trò của người này vừa được một quản trị khác thay đổi. Tải lại để xem bản mới nhất rồi lưu lại.',
      );
    }
    const hienCo = ganVaiTro.get(userId) ?? [];
    if (userId === TOI && roleIds.some((r) => !hienCo.includes(r))) {
      throw loi('SELF_ROLE_CHANGE_FORBIDDEN', 403, 'Không tự thêm vai trò cho chính mình được. Nhờ một quản trị khác của tổ chức.');
    }
    const sai = roleIds.flatMap((r, i) =>
      cacVaiTro.some((x) => x.id === r) ? [] : [{ pointer: `#/role_ids/${i}`, detail: `Vai trò ${r} không còn.` }],
    );
    if (sai.length > 0) {
      throw loi('ROLE_NOT_FOUND', 422, 'Có vai trò vừa bị xoá. Tải lại danh sách vai trò rồi chọn lại.', sai);
    }
    ganVaiTro.set(userId, [...new Set(roleIds)]);
    tangPhienBan(userId);
    const kq: UserRoles = { user_id: userId, role_ids: [...(ganVaiTro.get(userId) ?? [])] };
    return { ...kq, etag: etag(userId) };
  },
};
